<template>
  <a-modal
    :visible="visible"
    title="Update Progress"
    @ok="handleSubmit"
    @cancel="handleCancel"
    :confirm-loading="submitting"
    destroy-on-close
  >
    <a-form :form="form">
      <!-- Hidden Inputs -->
      <a-input
        type="hidden"
        v-if="item && item.type === 'topic'"
        v-decorator="['topic_id', { initialValue: item.id }]"
      />
      <a-input
        type="hidden"
        v-if="item && item.type === 'sub-topic'"
        v-decorator="['sub_topic_id', { initialValue: item.id }]"
      />

      <!-- Teacher -->
      <a-form-item label="Teacher" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-select
          show-search
          option-filter-prop="children"
          v-decorator="[
            'teacher_id',
            {
              rules: [{ required: true, message: 'Please select a teacher' }],
              initialValue: defaultTeacherId
            }
          ]"
          placeholder="Select a teacher"
          :disabled="!isAdmin && lessonPlan.tracking_mode !== 'open'"
        >
          <a-select-option
            v-for="staff in staffOptions"
            :key="staff.id"
            :value="staff.id"
          >
            {{ staff.first_name }} {{ staff.last_name }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <!-- Section -->
      <a-form-item label="Section" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-select
          v-decorator="[
            'section_id',
            { rules: [{ required: true, message: 'Please select a section' }] }
          ]"
          placeholder="Select a section"
          :disabled="!lessonPlan"
        >
          <a-select-option
            v-for="section in sections"
            :key="section.id"
            :value="section.id"
          >
            {{ section.name }}
          </a-select-option>
        </a-select>
      </a-form-item>

      <!-- Period -->
      <a-form-item label="Period" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-select
          v-decorator="[
            'period',
            { rules: [{ required: true, message: 'Please select a period' }] }
          ]"
          placeholder="Select a period"
        >
          <a-select-option :value="1">1st Period</a-select-option>
          <a-select-option :value="2">2nd Period</a-select-option>
          <a-select-option :value="3">3rd Period</a-select-option>
          <a-select-option :value="4">4th Period</a-select-option>
          <a-select-option :value="5">5th Period</a-select-option>
          <a-select-option :value="6">6th Period</a-select-option>
        </a-select>
      </a-form-item>

      <!-- Status -->
      <a-form-item label="Status" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-select
          v-decorator="[
            'status',
            { rules: [{ required: true, message: 'Please select a status' }] }
          ]"
        >
          <a-select-option value="Not Started">Not Started</a-select-option>
          <a-select-option value="Started">Started</a-select-option>
          <a-select-option value="In Progress">In Progress</a-select-option>
          <a-select-option value="Completed">Completed</a-select-option>
        </a-select>
      </a-form-item>

      <!-- Details -->
      <a-form-item label="Details" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-textarea
          rows="3"
          v-decorator="['details']"
          placeholder="What was covered?"
        />
      </a-form-item>

      <!-- Remarks -->
      <a-form-item label="Remarks" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-textarea
          rows="2"
          v-decorator="['remarks']"
          placeholder="Any extra notes?"
        />
      </a-form-item>

      <!-- Date -->
      <a-form-item
        label="Date"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-date-picker v-decorator="['date', { initialValue: moment() }]" style="width: 100%" />
      </a-form-item>

      <!-- Substitute Checkbox -->
      <a-form-item label="Substitute" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-checkbox v-decorator="['is_substitute', { valuePropName: 'checked', initialValue: false }]">
          I am taking this class as a substitute
        </a-checkbox>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script>
import moment from 'moment'

export default {
  name: 'UpdateProgressModal',
  props: {
    visible: Boolean,
    item: { type: Object, default: () => ({}) },
    lessonPlan: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      form: this.$form.createForm(this),
      submitting: false,
      loading: false,
      staffOptions: [],
      defaultTeacherId: undefined,
      sections: [],
      isAdmin: false,
    }
  },
  watch: {
    visible(val) {
      if (val) {
        const user = this.$store.getters.user
        this.isAdmin = user && ['superadmin', 'admin'].includes(user.role)

        if (this.lessonPlan && this.lessonPlan.class_id) {
          this.getSection()
        }
        if (this.lessonPlan && this.lessonPlan.subject_id) {
          this.getStaff()
        }
      } else {
        this.form.resetFields()
        this.defaultTeacherId = undefined
        this.sections = []
      }
    },
  },
  methods: {
    moment,
    getStaff() {
      // Fetch teachers for this subject
      this.$http
        .get('/api/lesson-plan/teacher/subject-teachers', { params: { subject_id: this.lessonPlan.subject_id } })
        .then(({ data }) => {
          this.staffOptions = data.data
          const user = this.$store.getters.user
          if (user && user.id) {
            const currentStaff = this.staffOptions.find(s => Number(s.user_id) === Number(user.id))
            if (currentStaff) {
              this.defaultTeacherId = currentStaff.id
            }
          }

          if (this.defaultTeacherId) {
            this.form.setFieldsValue({ teacher_id: this.defaultTeacherId })
          }
        })
        .catch(err => {
          this.$notification.error({ message: 'Failed to load staff list' })
          console.error(err)
        })
    },
    getSection() {
      this.$http
        .get('/api/lesson-plan/teacher/sections-for-class', { params: { class_id: this.lessonPlan.class_id } })
        .then(({ data }) => {
          this.sections = data.data
        })
        .catch(err => {
          this.$notification.error({ message: 'Failed to load sections' })
          console.error(err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSubmit() {
      this.form.validateFields((err, values) => {
        if (!err) {
          if (this.item && this.item.type === 'topic') {
            values.topic_id = this.item.id
          } else if (this.item && this.item.type === 'sub-topic') {
            values.sub_topic_id = this.item.id
          }
          if (values.date) {
            values.date = values.date.format('YYYY-MM-DD')
          }

          this.submitting = true
          this.$http
            .post('/api/lesson-plan/teacher/progress', values)
            .then(() => {
              this.$message.success('Progress updated successfully!')
              this.$emit('updated')
              this.handleCancel()
            })
            .catch(() => {
              this.$notification.error({ message: 'Failed to update progress' })
            })
            .finally(() => {
              this.submitting = false
            })
        }
      })
    },
    handleCancel() {
      this.form.resetFields()
      this.$emit('close')
    },
  },
}
</script>
